interface AssetMeta {
    asset_meta_uid: string
    meta_title: string
    meta_value: string
    placing: number
}

interface AssetIncentive {
    asset_inc_uid: string
    incentives_amount: number
    incentives_limit: number
    incentives_title: string
    incentives_description: string
}

export enum CampaignRegulatoryRequirement {
    DEFAULT = 0, // 0 = Default
    ALTFG = 1, // 1 = AltFG -> Alternativfinanzierungsgesetz
    VERMANLG = 2, // 2 = VermAnlG -> Vermögensanlagengesetz
    WPHG = 3, // 3 = WpHG-Aktien -> Wertpapierhandelsgesetz, Aktien
    ECSP = 4, // 4 = ECSP Regulatory -> European Crowdfunding Service Providers
    STOCK_EMISSION = 5, // 5 = Stock Emission -> Stock Emission
}

export enum ClearingSystem {
    CI = 'CI',
    CF = 'CF',
    ICO = 'ICO',
    RISE = 'RISE',
    EMISSION = 'EMISSION',
    STOCK_EMISSION = 'STOCK_EMISSION',
}

export enum AgioDisagioType {
    PERCENT = 0,
    FIXED_VALUE = 1,
    FIXED_VALUE_PER_SHARE = 2,
}

export enum AssetCategory {
    DEFAULT = 0,
    STARTUP = 1,
    SME = 2,
    REAL_ESTATE = 3,
    SPECIAL = 4,
    STOCK_EMISSION = 5,
}

export enum AgioDisagioActivationType {
    DEACTIVATED = 0,
    AGIO_ACTIVATED = 1,
    DISAGIO_ACTIVATED = 2,
}

interface TeamMember {
    id: string
    name: string
    title: string
    member_image: string
    member_srcset: string | null
    bio: string
    position: number
}

interface Disclaimer {
    id: string
    platform_uid: string
    countries_applied: string[]
    countries_not_applied: string[] | null
    risk_content: string
    risk_title: string
    language: string
}

export interface ContractDocument {
    id: string
    name: string
    description: string
    url: string
}

interface Broker {
    broker_status: boolean
    broker_disclaimer: string
    broker_field_required: boolean
}

interface AssetData {
    asset_var_uid: string
    language: string
    nyala_active: boolean
    nyala_custody_provider: number | null
    asset_title: string
    asset_subtitle: string
    asset_summary: string
    asset_description: string
    investment_summary: string
    thumb_image: string
    thumb_srcset: string | null
    hero_image: string
    hero_imageset: string | null
    asset_video: string | null
    asset_excerpt: string
    funding_start: string
    funding_end: string
    show_amount_slider: boolean
    status: number
    platform_name: string
    badge: string | null
    funding_stats_visible: boolean
    asset_var_password: string | null
    regulatory_requirements: number
    broker: Broker
    agio_disagio: number
    agio_disagio_rate: string | null
    agio_disagio_title: string | null
    agio_disagio_disclaimer: string | null
    agio_disagio_type: number
    agio_disagio_info_text: string | null
    savings_plan: boolean
    savings_plan_description: string | null
    voucher_rate: number
    voucher_code: string
    external_project_url: string | null
    additional_fields: any | null
    asset_uid: string
    investment_min: number
    investment_max: number
    contract_period: string | null
    contract_range: string
    funding_min: number
    funding_max: number
    step_level: number
    installment_investment: boolean
    installment_payout: boolean
    minimum_initial_payment: number | null
    maximum_initial_payment: number | null
    initial_payment_step: number | null
    minimum_installment: number | null
    maximum_installment: number | null
    installment_step: number | null
    minimum_monthly_rates: number | null
    maximum_monthly_rates: number | null
    minimum_yearly_rates: number | null
    maximum_yearly_rates: number | null
    currency_string: string
    currency: string
    tags: string[] | null
    country_name: string
    asset_location: {
        city: string
        state: string
        country: string
    }
    company_name: string
    company_logo: string
    company_logo_srcset: string | null
    company_details: {
        address: string
        city: string
        postal_code: string
        state: string
        country: string
        phone_number: string
        email: string
        website: string
    }
    clearing_system: {
        name: ClearingSystem
    }
    bic: string
    iban: string
    category: number
    interest_rates: {
        interest_rate: number
        interest_rate_type: number
    }[]
    funding_count: number
    funding_total: number
    funding_amount: number
    payment_methods: {
        credit_card: boolean
        wire_transfer: boolean
    }
    document_count: number
    signature_manual: boolean
    signature_sproof: boolean
    signature_signteq: boolean
    signature_legal_notice: string | null
    appropriateness_test_minimum_experience_and_knowledge: number
    appropriateness_test_minimum_experience_investment_services: number
    contract_data_documents: ContractDocument[]
    reporting_documents: any[] // You can replace "any" with a specific type if available.
    needs_contract_type_selection: boolean
    contract: any | null // You can replace "any" with a specific type if available.
    voucher_contract: any | null // You can replace "any" with a specific type if available.
    stock_increments: number
    minimum_investment: number
    maximum_total_investment_value: number
    price_per_stock: number
    number_of_stocks: number
    minimum_investment_type: 'shares' | 'amount'
    deactivate_payment_account: boolean
    hide_payment_info: boolean
}

interface Order {
    first_name: string | null
    last_name: string | null
    company_name: string | null
    account_type: string
    amount: number
    currency: string
    created_on: string
}

interface CampaignData {
    assets_meta: AssetMeta[]
    assets_incentives: AssetIncentive[]
    team_members: TeamMember[]
    disclaimers: Disclaimer[]
    asset_data: AssetData
    orders: Order[]
    permalink: string
    asset_flag: string
    remaining: string
    base_interest_rate: number
    company_address: string
    ip: string
    category_image: string | null
    category_name: string | null
}

export function getCampaignData(): CampaignData {
    if (typeof window === 'undefined' || typeof window.campaign === 'undefined') {
        console.error('Campaign data is not available in the current context.')
        return null
    }
    return window.campaign
}
